import { useState } from "react";
import { Trash2, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, toList } from "../../lib/api";
import { useApi } from "../../hooks/useApi";
import { Panel, Loader, ErrorNote, Empty, Btn, shortDate } from "./kit";
import { Chips } from "./Form";

const TYPES = [["note", "General"], ["practice", "Practice"], ["game", "Game"], ["academic", "Academics"], ["character", "Character"]];
const label = (t) => TYPES.find(([k]) => k === t)?.[1] ?? "General";

/** Private coach notes on a player (stored in the GAMEDAY app; coaches only). */
export const CoachNotes = ({ tid, pid }) => {
  const notes = useApi(`/teams/${tid}/players/${pid}/notes`);
  const [body, setBody] = useState("");
  const [type, setType] = useState("note");
  const [rating, setRating] = useState(null);
  const [busy, setBusy] = useState(false);
  const list = toList(notes.data, ["notes"]).sort((a, b) => String(b.created_at ?? b.date).localeCompare(String(a.created_at ?? a.date)));

  const add = async (e) => {
    e.preventDefault();
    if (!body.trim()) return toast.error("Write a note first");
    setBusy(true);
    try {
      await api.post(`/teams/${tid}/notes`, { player_id: pid, type, body: body.trim(), rating });
      setBody(""); setRating(null);
      toast.success("Note saved");
      notes.reload();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const remove = async (n) => {
    if (!window.confirm("Delete this note?")) return;
    try { await api.delete(`/teams/${tid}/notes/${n.id}`); notes.reload(); } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[2fr_3fr]">
      <Panel>
        <h3 className="mb-1 font-display text-xl font-bold uppercase text-white">Add a note</h3>
        <p className="mb-4 flex items-center gap-1.5 text-xs text-slate-400"><Lock className="h-3 w-3" />Only coaches can see these.</p>
        <form onSubmit={add} className="space-y-4">
          <Chips testid="note-type" options={TYPES} value={type} onChange={setType} />
          <textarea data-testid="note-body-input" value={body} onChange={(e) => setBody(e.target.value)} rows={5} maxLength={2000} placeholder="Great help-side rotations today. Keep working on left-hand finishes."
            className="w-full rounded-xl border border-[#1E293B] bg-[#0F172A] px-3.5 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-[#39FF14] focus:outline-none" />
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-1" aria-label="Rating">
              <span className="mr-1 font-mono-gd text-[11px] uppercase text-slate-400">Rating</span>
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" data-testid={`note-rating-${n}`} onClick={() => setRating(rating === n ? null : n)}
                  className={`h-7 w-7 rounded-full font-mono-gd text-xs ${rating && n <= rating ? "bg-[#39FF14] text-[#0A0E1A]" : "bg-[#162032] text-slate-300"}`}>{n}</button>
              ))}
            </div>
            <Btn type="submit" disabled={busy} data-testid="note-save">{busy ? "Saving…" : "Save note"}</Btn>
          </div>
        </form>
      </Panel>
      <Panel>
        <h3 className="mb-4 font-display text-xl font-bold uppercase text-white">Coach notes</h3>
        {notes.loading && !notes.data ? <Loader /> : notes.error ? <ErrorNote error={notes.error} /> : !list.length ? <Empty title="No notes yet" /> : (
          <ul className="space-y-3">
            {list.map((n) => (
              <li key={n.id} data-testid="coach-note-item" className="rounded-xl border border-[#1E293B] bg-[#162032] p-4">
                <div className="flex items-start justify-between gap-3">
                  <p className="whitespace-pre-wrap text-sm text-slate-100">{n.body}</p>
                  <button data-testid={`note-delete-${n.id}`} onClick={() => remove(n)} className="shrink-0 rounded-full p-1.5 text-slate-400 hover:text-red-300" aria-label="Delete note"><Trash2 className="h-4 w-4" /></button>
                </div>
                <p className="mt-2 font-mono-gd text-[11px] uppercase tracking-wider text-slate-400">{label(n.type)}{n.rating ? ` · ${n.rating}/5` : ""} · {shortDate(n.created_at ?? n.date)}</p>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
};
